<template>
    <b-form>
        <b-form-group
            v-if="isShowSource"
            :label-for="`source`"
            :label-cols="labelCols"
            label="Source:"
        >
            <b-form-select
                v-model="source"
                :options="sources"
                value-field="value"
                text-field="text"
                name="source"
                @change="sourceChange"
            ></b-form-select>
        </b-form-group>

        <b-form-group
            :label-for="`attachment`"
            :label-cols="labelCols"
            :label="attachmentLabel"
        >
            <b-form
                v-if="source == 0"
                v-model="data.file"
                enctype="multipart/form-data"
            >
                <div class="dropzone">
                    <input
                        id="file"
                        ref="file"
                        type="file"
                        multiple
                        :accept="accept"
                        class="input-file"
                        @change="fileChange"
                    />
                    <p>Drop files here or click to upload.</p>
                </div>
                <div
                    v-for="(item, index) in file"
                    :key="index"
                    style="margin: 10px 0;"
                >
                    <span style="display: inline;">
                        <i
                            v-on:click="removeFile(index)"
                            class="famfamfam-silk cus-delete"
                            style="cursor: pointer;"
                        ></i>
                        {{ item.name }}
                    </span>
                </div>
            </b-form>

            <b-form v-else-if="source == 1">
                <vue-select
                    v-model="data.templates"
                    placeholder="Select attachment(s) (required)"
                    :options="
                        _.difference(filteredTemplateOptions, data.templates)
                    "
                    :label="'name'"
                    :clearable="true"
                    class="show-actions"
                    @change="fileChange"
                    multiple
                >
                    <template v-slot:open-indicator>
                        <span></span>
                    </template>
                </vue-select>
            </b-form>

            <b-form v-else-if="source == 2">
                <vet-select
                    module="articles"
                    placeholder="Articles (required)"
                    v-model="data.articles"
                    :multiple="true"
                    :excludedId="excludedIds"
                    name="articles"
                />
            </b-form>
        </b-form-group>
    </b-form>
</template>
<style lang="scss">
.dropzone {
    outline: 1px dashed #ccc;
    background: #fff;
    color: dimgray;
    padding: 0;
    min-height: 60px;
    position: relative;
    cursor: pointer;
}

.input-file {
    opacity: 0;
    width: 100%;
    height: 50px;
    position: absolute;
    cursor: pointer;
}

.dropzone p {
    font-size: 12px;
    color: #aaa;
    text-align: center;
    padding: 20px 0;
}
</style>
<script>
import S3 from "aws-s3-pro";
import vueSelect from "vue-select";
import { forEach, get } from "lodash";
import MIMES from "@/module/commons/mimes.json";
import mimeTypes from "mime-types";
import TemplateService from "@/services/templates.js";
import VetSelect from "@/module/commons/vet-select";

export default {
    name: "AttachmentField",
    components: {
        vueSelect,
        VetSelect
    },
    props: {
        labelCols: {
            type: Number,
            default: 4
        },
        isShowSource: {
            type: Boolean,
            default: true
        },
        accept: {
            type: String,
            default: "*"
        },
        attachmentLabel: {
            type: String,
            default: "Attachment(s):"
        }
    },
    data() {
        return {
            file: [],
            fileList: [],
            mimes: MIMES,
            data: {
                file: null,
                url: [],
                templates: [],
                articles: []
            },
            source: 0,
            sources: [
                { value: 0, text: "Upload new" },
                { value: 1, text: "Attachments (Library)" }
            ],
            templateOptions: [],
            excludedIds: []
        };
    },
    computed: {
        filteredTemplateOptions() {
            if (this.accept === "*") {
                return this.templateOptions;
            } else {
                return this.templateOptions
                    .map(attachment => ({
                        ...attachment,
                        mime: mimeTypes.lookup(attachment.attachment_filename)
                    }))
                    .filter(
                        attachment =>
                            attachment.mime.indexOf(
                                this.accept.split("/")[0]
                            ) === 0
                    );
            }
        }
    },
    watch: {
        "data.file"() {
            let self = this;
            if (self.$refs.file) {
                forEach(self.$refs.file.files, function (data) {
                    let ext = data.name.split(".").pop().toLowerCase();
                    if (get(self.mimes, ext)) {
                        self.file.push(data);
                    } else {
                        self._message(" File type not permitted.", "danger");
                    }
                });
            }
            this.isMaxFileSize();
        },
        "data.templates"() {
            this.fileChange(event);
        },
        "data.articles"() {
            this.fileChange(event);
        }
    },
    mounted() {
        this.getAttachmentTemplates(), this.extensionCheck();
    },
    methods: {
        async getFile(params) {
            let self = this;
            self.loading = true;
            let patient_id = params.patient_id ? params.patient_id : null;
            let client_id = params.client_id ? params.client_id : null;
            let s3Path =
                this._s3Path(
                    params.module.toLowerCase(),
                    params.module === this.MODULE.CLIENT
                        ? client_id
                        : patient_id
                ) + "attachments";
            let promises = [];
            let config = {
                dirName: s3Path,
                bucketName: process.env.S3_BUCKET,
                region: process.env.S3_REGION,
                accessKeyId: process.env.S3_KEY,
                secretAccessKey: process.env.S3_SECRET,
                s3Url: process.env.S3_URI
            };
            const S3Client = new S3(config);
            // this for upload new
            if (!self.source) {
                if (this.isMaxFileSize()) {
                    // check max file size
                    forEach(self.file, function (data) {
                        let parts = self._get_fileparts(data.name);
                        let fileObj = {
                            title:
                                self._sanitizeFilename(parts[0], true) +
                                "." +
                                parts[1],
                            filename:
                                self._sanitizeFilename(parts[0], true) +
                                "_" +
                                self.uniqid(),
                            ext: parts[1],
                            lab_id: params.lab_id,
                            record_id: params.lab_id
                                ? params.lab_id
                                : params.record_id
                                ? params.record_id
                                : null,
                            summary: params.summary ? params.summary : null,
                            source_id: params.source_id
                                ? params.source_id
                                : null,
                            source_type: params.source_type
                                ? params.source_type
                                : null,
                            patient_id: patient_id,
                            client_id: client_id,
                            soap_id: params.soap_id ? params.soap_id : null
                        };
                        promises.push(
                            S3Client.uploadFile(data, fileObj.filename).then(
                                result => {
                                    if (result.status === 204) {
                                        return {
                                            result,
                                            update: self.updateAttachment(
                                                fileObj
                                            )
                                        };
                                    }
                                }
                            )
                        );
                    });
                }
                return Promise.all(promises);
            } else if (self.source == 2) {
                if (self.data.articles.length > 0) {
                    let formData = [];
                    forEach(self.data.articles, function (article) {
                        let targetFile =
                            self._s3Path(
                                params.module.toLowerCase(),
                                params.module === self.MODULE.CLIENT
                                    ? client_id
                                    : patient_id
                            ) + "attachments/";

                        formData.push({
                            articleID: article.id,
                            title: article.label + ".pdf",
                            filename:
                                self._sanitizeFilename(article.label, true) +
                                "_" +
                                self.uniqid() +
                                ".pdf",
                            target_file: targetFile,
                            summary: params.summary ? params.summary : null,
                            soap_id: params.soap_id ? params.soap_id : null,
                            source_id: params.source_id
                                ? params.source_id
                                : null,
                            source_type: params.source_type
                                ? params.source_type
                                : null,
                            patient_id: patient_id,
                            client_id: client_id
                        });
                    });
                    promises.push(self.saveArticle(formData));
                }
                return Promise.all(promises);
            } else {
                if (self.data.templates.length >= 1) {
                    forEach(self.data.templates, function (template) {
                        let formData = [];
                        let parts = self._get_fileparts(
                            template.attachment_title
                        );

                        let fileObj = {
                            title:
                                self._sanitizeFilename(parts[0], true) +
                                "." +
                                parts[1],
                            filename:
                                self._sanitizeFilename(parts[0], true) +
                                "_" +
                                self.uniqid(),
                            ext: parts[1],
                            template_id: template.id,
                            record_id: params.lab_id
                                ? params.lab_id
                                : params.record_id
                                ? params.record_id
                                : null,
                            summary: params.summary ? params.summary : null,
                            soap_id: params.soap_id ? params.soap_id : null,
                            source_id: params.source_id
                                ? params.source_id
                                : null,
                            source_type: params.source_type
                                ? params.source_type
                                : null,
                            patient_id: patient_id,
                            client_id: client_id
                        };

                        let targetFile =
                            self._s3Path(
                                params.module.toLowerCase(),
                                params.module === self.MODULE.CLIENT
                                    ? client_id
                                    : patient_id
                            ) +
                            "attachments/" +
                            fileObj.filename +
                            "." +
                            fileObj.ext;
                        let sourceFile =
                            self._s3Path(
                                "business",
                                self._setting.business.id
                            ) +
                            "attachments/" +
                            template.attachment_filename;

                        formData = {
                            source: sourceFile,
                            target: targetFile
                        };
                        promises.push(self.copyAttachment(formData, fileObj));
                        // return self.copyAttachment(formData, fileObj);
                    });
                }
                return Promise.all(promises);
            }
        },
        isMaxFileSize() {
            let self = this;
            let totalSize = 0;
            forEach(self.file, function (data) {
                totalSize = totalSize + data.size;
            });

            if (totalSize > 200 * 1000 * 1000) {
                self._message(
                    " Total file size exceed limit (200.0 MB).",
                    "danger"
                );
                self.file = [];
                return false;
            } else {
                return true;
            }
        },
        async updateAttachment(fileObj) {
            let params = {
                title: fileObj.title,
                filename: fileObj.filename + "." + fileObj.ext,
                isactive: 1,
                is_dicom: 0,
                is_s3: 1,
                patient_id: fileObj.patient_id,
                client_id: fileObj.client_id,
                record_id: fileObj.record_id,
                lab_id: fileObj.lab_id,
                template_id: fileObj.template_id,
                summary: fileObj.summary,
                source_id: fileObj.source_id,
                source_type: fileObj.source_type,
                soap_id: fileObj.soap_id
            };
            let response = await this.$http.post(
                process.env.BARRACUDA + "record/save-attachment",
                params
            );
            return response;
        },
        removeFile(index) {
            this.file.splice(index, 1);

            this.$emit("onFileChange", {
                hasFile: this.file.length > 0
            });
        },
        fileChange(event) {
            let self = this;
            let fileCount = 0;
            if (self.source == 1) {
                fileCount = self.data.templates.length;
            } else if (self.source == 2) {
                // VET-3393: to exclude selected options from lifelearn articles
                this.excludedIds = self.data.articles;
                fileCount = self.data.articles.length;
            } else {
                fileCount = event.target.files.length;
            }

            this.$emit("onFileChange", {
                source: self.source,
                hasFile: fileCount > 0
            });
        },
        sourceChange() {
            let self = this;
            let fileCount = 0;

            if (self.source) {
                fileCount = self.data.templates.length;
            } else {
                fileCount = self.file.length;
            }

            this.$emit("on-source-change", {
                source: self.source,
                hasFile: fileCount > 0
            });
        },
        async getAttachmentTemplates() {
            TemplateService.getAttachments().then(templates => {
                this.templateOptions = templates;
            });
        },
        async extensionCheck() {
            if (
                this._isExtensionEnabled("LIFE_LEARN") &&
                this.accept !== "image/*"
            ) {
                this.sources.push({
                    value: 2,
                    text: "LifeLearn ClientEd"
                });
            }
        },
        async copyAttachment(formData, fileObj) {
            let self = this;
            await self.$http
                .post(process.env.BARRAMUNDI + "record/copy-attachment", {
                    formData
                })
                .then(response => {
                    if (response) {
                        return self.updateAttachment(fileObj);
                    }
                })
                .catch(error => {
                    self._message(
                        "Error : Unable to attach the file",
                        "danger"
                    );
                    console.error(error);
                });
        },
        async saveArticle(formData) {
            let self = this;
            await self.$http
                .post(
                    process.env.BARRAMUNDI +
                        "record/attachment/lifelearn/get-article",
                    {
                        formData
                    }
                )
                .then(response => {
                    return response;
                })
                .catch(error => {
                    self._message(
                        "Error : Unable to attach the file",
                        "danger"
                    );
                    console.error(error);
                });
        },
        resetFile() {
            this.file = [];
        }
    }
};
</script>
